// packages/web/src/hooks/useBannerState.ts
import { useState, useEffect } from 'react';

interface BannerState {
  show: boolean;
  checked: boolean;
}

const BANNER_STATE_KEY = 'publishScheduleBannerState';

export const useBannerState = (userId: string | undefined) => {
  const [shouldShow, setShouldShow] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setShouldShow(false);
      setIsLoading(false);
      return;
    }

    const stateKey = `${BANNER_STATE_KEY}_${userId}`;
    const savedState = localStorage.getItem(stateKey);
    
    if (savedState) {
      const state: BannerState = JSON.parse(savedState);
      setShouldShow(state.show);
      setIsLoading(false);
    } else {
      // First time - initialize state
      const initialState: BannerState = { show: true, checked: false };
      localStorage.setItem(stateKey, JSON.stringify(initialState));
      setShouldShow(true);
      setIsLoading(false);
    }
  }, [userId]);

  const checkSlots = async (hasSlots: boolean) => {
    if (!userId) return;

    const stateKey = `${BANNER_STATE_KEY}_${userId}`;
    const savedState = localStorage.getItem(stateKey);
    const state: BannerState = savedState ? JSON.parse(savedState) : { show: true, checked: false };

    if (!state.checked) {
      // First check - update based on slots
      const newState: BannerState = {
        show: !hasSlots,
        checked: true,
      };
      localStorage.setItem(stateKey, JSON.stringify(newState));
      setShouldShow(!hasSlots);
    }
  };

  const onSlotCreated = () => {
    if (!userId) return;

    const stateKey = `${BANNER_STATE_KEY}_${userId}`;
    const newState: BannerState = { show: false, checked: true };
    localStorage.setItem(stateKey, JSON.stringify(newState));
    setShouldShow(false);
  };

  const onClose = () => {
    if (!userId) return;

    const stateKey = `${BANNER_STATE_KEY}_${userId}`;
    const savedState = localStorage.getItem(stateKey);
    const state: BannerState = savedState ? JSON.parse(savedState) : { show: true, checked: false };
    
    const newState: BannerState = { ...state, show: false };
    localStorage.setItem(stateKey, JSON.stringify(newState));
    setShouldShow(false);
  };

  return { shouldShow, isLoading, checkSlots, onSlotCreated, onClose };
};